// modules
import { sortBy } from 'lodash'
import moment from 'moment'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import {
  setAssignedMedicationList,
  setCarePlanActiveTab,
  setDosageTypes,
  setIntakeList,
  setMedicationActiveTab,
  setMedicationList,
  setMedicationRuleList,
  setOrderingList,
  setPatientDashboardMedicationList,
  setPatientMedicationList,
  setPatientMedicationValue,
  setPharmacyList
} from 'src/store/modules/patient/medications'

// constants
import {
  DATE_TIME_FORMAT_FOR_SERVER,
  EXPORT_REPORT_PATIENT_INTAKES,
  INTAKE_DELETE_MESSAGE,
  INTAKE_PATIENT_GET,
  INTAKE_PATIENT_PATCH_DELETE,
  INTAKE_PATIENT_POST,
  INTAKE_PATIENT_WITH_UUID_POST,
  INTAKE_SAVE_MESSAGE,
  MEDICATION_ALERT_STATUS_DECISION,
  MEDICATION_DASHBOARD_GET,
  MEDICATION_DELETE_MESSAGE,
  MEDICATION_DOSAGES_TYPES,
  MEDICATION_GET,
  MEDICATION_LIST_ASSIGNED,
  MEDICATION_PATIENT_COPY,
  MEDICATION_PATIENT_CREATE_INTAKE,
  MEDICATION_PATIENT_GET,
  MEDICATION_PATIENT_LIST_GET,
  MEDICATION_PATIENT_POST,
  MEDICATION_RULE_GET,
  MEDICATION_TAB_LIST,
  ORDERING_GET,
  PHARMACY_GET
} from 'src/constants'
import { showSuccessModal } from 'src/services/dialogs'
import { exportReport } from 'src/utils/Functions'

export const fetchPatientMedicationList =
  ({ query = {} }) =>
  async (dispatch) => {
    try {
      const { items = [], meta = {} } = await httpClient.request({
        method: 'get',
        url: {
          pathname: MEDICATION_PATIENT_LIST_GET,
          query
        }
      })

      dispatch(setPatientMedicationList(items))

      return meta
    } catch (err) {
      catchError(err)
    }
  }

export const fetchDasboardMedicationList =
  ({ params = {}, query = {} }) =>
  async (dispatch) => {
    try {
      const { items = [], meta = {} } = await httpClient.request({
        method: 'get',
        url: {
          pathname: MEDICATION_DASHBOARD_GET,
          params,
          query
        }
      })

      dispatch(setPatientDashboardMedicationList(items))

      return meta
    } catch (err) {
      catchError(err)
    }
  }

export const createMedicationPatientIntake =
  (config = {}) =>
  async () => {
    try {
      const { params = {}, payload = {} } = config

      await httpClient.post(
        {
          pathname: MEDICATION_PATIENT_CREATE_INTAKE,
          params
        },
        payload
      )

      showSuccessModal({
        content: 'Intake successfully created!',
        centered: true,
        keyboard: false
      })
    } catch (err) {
      catchError(err)
    }
  }

export const fetchPatientMedicationValue =
  ({ params = {} }) =>
  async (dispatch) => {
    try {
      const item = await httpClient.request({
        method: 'get',
        url: {
          pathname: MEDICATION_PATIENT_GET,
          params
        }
      })

      dispatch(setPatientMedicationValue(item))
    } catch (err) {
      catchError(err)
    }
  }

export const savePatientMedicationValue =
  ({ payload = {} }, patientUuid, history, isCarePlan = false) =>
  async (dispatch) => {
    try {
      await httpClient.request({
        method: 'post',
        url: { pathname: MEDICATION_PATIENT_POST },
        payload
      })

      if (isCarePlan) {
        dispatch(setCarePlanActiveTab('medication'))
        history.push(`/patients/${patientUuid}/care-plan`)
      } else {
        dispatch(setMedicationActiveTab(MEDICATION_TAB_LIST[1]))
        history.push(`/patients/${patientUuid}/medication`)
      }

      return true
    } catch (err) {
      catchError(err, {
        ignoredValuesList: ['frequency.reminders'],
        errorFieldsMap: {
          dosage: 'Strength',
          Dosage: 'Strength'
        }
      })

      return false
    }
  }

export const updatePatientMedicationValue =
  ({ params = {}, payload = {} }, patientUuid, history, isCarePlan = false) =>
  async (dispatch) => {
    try {
      await httpClient.request({
        method: 'patch',
        url: {
          pathname: MEDICATION_PATIENT_GET,
          params
        },
        payload
      })

      if (isCarePlan) {
        dispatch(setCarePlanActiveTab('medication'))
        history.push(`/patients/${patientUuid}/care-plan`)
      } else {
        dispatch(setMedicationActiveTab(MEDICATION_TAB_LIST[1]))
        history.push(`/patients/${patientUuid}/medication`)
      }

      return true
    } catch (err) {
      catchError(err, {
        ignoredValuesList: ['frequency.reminders'],
        errorFieldsMap: {
          dosage: 'Strength',
          Dosage: 'Strength'
        }
      })

      return false
    }
  }

export const deletePatientMedicationValue =
  ({ params = {} }) =>
  async () => {
    try {
      await httpClient.request({
        method: 'delete',
        url: {
          pathname: MEDICATION_PATIENT_GET,
          params
        }
      })

      showSuccessModal({
        content: MEDICATION_DELETE_MESSAGE,
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const fetchIntakeList =
  ({ params = {}, query = {} }) =>
  async (dispatch) => {
    try {
      const { items = [], meta = {} } = await httpClient.request({
        method: 'get',
        url: {
          pathname: INTAKE_PATIENT_GET,
          params,
          query
        }
      })

      dispatch(setIntakeList(items))

      return meta
    } catch (err) {
      catchError(err)
    }
  }

export const saveIntakeValue =
  ({ payload = {} }) =>
  async () => {
    try {
      await httpClient.request({
        method: 'post',
        url: { pathname: INTAKE_PATIENT_POST },
        payload
      })

      showSuccessModal({
        content: INTAKE_SAVE_MESSAGE,
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const saveIntakeWithUuid =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: INTAKE_PATIENT_WITH_UUID_POST,
          params
        },
        payload
      })

      showSuccessModal({
        content: INTAKE_SAVE_MESSAGE,
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const updateIntakeValue =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'patch',
        url: {
          pathname: INTAKE_PATIENT_PATCH_DELETE,
          params
        },
        payload
      })

      showSuccessModal({
        content: INTAKE_SAVE_MESSAGE,
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const deleteIntakeValue =
  ({ params = {} }) =>
  async () => {
    try {
      await httpClient.request({
        method: 'delete',
        url: {
          pathname: INTAKE_PATIENT_PATCH_DELETE,
          params
        }
      })

      showSuccessModal({
        content: INTAKE_DELETE_MESSAGE,
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const getMedicationList = async (config = {}) => {
  try {
    const { query = {} } = config
    const response = await httpClient.request({
      method: 'get',
      url: {
        pathname: MEDICATION_GET,
        query
      }
    })

    return response?.items || []
  } catch (err) {
    catchError(err)

    return []
  }
}

export const fetchMedicationList =
  (config = {}) =>
  async (dispatch) => {
    try {
      const medications = await getMedicationList(config)

      dispatch(setMedicationList(medications))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchAssignedMedicationList =
  ({ params = {} }) =>
  async (dispatch) => {
    try {
      const items = await httpClient.request({
        method: 'get',
        url: {
          pathname: MEDICATION_LIST_ASSIGNED,
          params
        }
      })

      dispatch(setAssignedMedicationList(sortBy(items, ['inventory:desc'])))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchMedicationRuleList =
  (config = {}) =>
  async (dispatch) => {
    const { query = {} } = config

    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: MEDICATION_RULE_GET,
          query
        }
      })

      dispatch(setMedicationRuleList(items))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchOrderingList =
  ({ params = {} }) =>
  async (dispatch) => {
    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: ORDERING_GET,
          params
        }
      })

      dispatch(setOrderingList(items))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchPharmacyList =
  ({ params = {}, query = {} }) =>
  async (dispatch) => {
    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: PHARMACY_GET,
          params,
          query
        }
      })

      dispatch(setPharmacyList(items))
    } catch (err) {
      catchError(err)
    }
  }

export const getDosageTypes = () => async (dispatch) => {
  try {
    const items = await httpClient.request({
      method: 'get',
      url: {
        pathname: MEDICATION_DOSAGES_TYPES
      }
    })

    dispatch(setDosageTypes(items))
  } catch (err) {
    catchError(err)
  }
}

export const exportIntakesReport = (config) => async () => {
  const updConfig = {
    pathname: EXPORT_REPORT_PATIENT_INTAKES,
    ...config
  }

  await exportReport(
    updConfig,
    `patient_intake_answers_${moment().format(DATE_TIME_FORMAT_FOR_SERVER)}.xls`
  )
}

export const copyMedicationPatient =
  (config = {}) =>
  async () => {
    const { params = {} } = config

    try {
      await httpClient.post({
        pathname: MEDICATION_PATIENT_COPY,
        params
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const getMedicationPatientAlertStatusDecision =
  (config = {}) =>
  async () => {
    const {
      pathname = MEDICATION_ALERT_STATUS_DECISION,
      params = {},
      payload = {}
    } = config

    try {
      const { askConfirmQuestion = false } = await httpClient.request({
        method: 'post',
        url: {
          pathname,
          params
        },
        payload
      })

      return { askConfirmQuestion, hasError: false }
    } catch (err) {
      catchError(err)

      return { askConfirmQuestion: false, hasError: true }
    }
  }
